import { 
  TrainingSummaryReportDto, 
  TrainingCompletionReportDto,
} from '../app/services/api/compliance/training/trainingTypes';

/**
 * Formats a date string to YYYY-MM-DD format
 */
function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toISOString().split('T')[0];
  } catch {
    return dateStr;
  }
}

/**
 * Formats a date string to YYYY-MM-DD HH:MM format
 */
function formatDateTime(dateStr: string): string {
  try {
    const iso = new Date(dateStr).toISOString(); // always UTC
    return `${iso.substring(0, 10)} ${iso.substring(11, 16)}`;
  } catch {
    return dateStr;
  }
}

/**
 * Escapes HTML special characters to prevent XSS
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Generates HTML preview for Training Summary Report
 */
export function generateSummaryHtmlPreview(report: TrainingSummaryReportDto): string {
  // Plain styling without colors
  const headerBg = '#ffffff';
  const tableHeaderBg = '#f5f5f5';
  const textColor = '#000000';
  const borderColor = '#cccccc';

  const moduleSummariesHtml = report.moduleSummaries && report.moduleSummaries.length > 0 ? `
    <h2>Module Performance</h2>
    <div class='table-scroll'>
    <table class='detail-table'>
        <thead>
            <tr>
                <th>Module</th>
                <th>Category</th>
                <th>Total</th>
                <th>Completed</th>
                <th>Completion Rate</th>
                <th>Avg Score</th>
                <th>Passed</th>
                <th>Failed</th>
            </tr>
        </thead>
        <tbody>
            ${report.moduleSummaries.map(m => `
            <tr>
                <td><strong>${escapeHtml(m.moduleName)}</strong></td>
                <td>${escapeHtml(m.categoryName)}</td>
                <td>${m.totalAssignments}</td>
                <td>${m.completedCount}</td>
                <td>
                    <span class='completion-rate'>
                        ${m.completionRate.toFixed(1)}%
                    </span>
                </td>
                <td>${m.averageScore.toFixed(1)}</td>
                <td style='font-weight: 600;'>${m.passedCount}</td>
                <td>${m.failedCount}</td>
            </tr>`).join('')}
        </tbody>
    </table>
    </div>` : '';

  const assignmentDetailsHtml = report.assignmentDetails && report.assignmentDetails.length > 0 ? `
    <h2>All Assignments</h2>
    <div class='table-scroll'>
    <table class='detail-table'>
        <thead>
            <tr>
                <th>Module</th>
                <th>Category</th>
                <th>Assigned Date</th>
                <th>Due Date</th>
                <th>Completed Date</th>
                <th>Status</th>
                <th>Score</th>
                <th>Attempts</th>
            </tr>
        </thead>
        <tbody>
            ${report.assignmentDetails.map(a => `
            <tr>
                <td><strong>${escapeHtml(a.moduleName)}</strong></td>
                <td>${escapeHtml(a.categoryName)}</td>
                <td>${formatDate(a.assignedDate)}</td>
                <td>${a.dueDate ? formatDate(a.dueDate) : '—'}</td>
                <td>${a.completedDate ? formatDate(a.completedDate) : '—'}</td>
                <td>
                    <span style='font-weight: 600;'>
                        ${escapeHtml(a.status)}
                    </span>
                </td>
                <td>${a.score === null ? '—' : a.score.toFixed(1)}</td>
                <td>${a.attemptCount}</td>
            </tr>`).join('')}
        </tbody>
    </table>
    </div>` : '';

  return `
<!DOCTYPE html>
<html lang='en'>
<head>
    <meta charset='utf-8'>
    <meta name='viewport' content='width=device-width, initial-scale=1.0'>
    <title>Training Summary Report</title>
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; background: #ffffff; padding: 20px; color: ${textColor}; }
        .container { max-width: 1800px; margin: 0 auto; background: #ffffff; padding: 40px; border: 1px solid ${borderColor}; }
        .header { background: === null ? '—' : a.score.toFixed(1)px -40px; padding: 30px 40px; border-bottom: 2px solid ${borderColor}; color: ${textColor}; }
        h1 { font-size: 28px; font-weight: 700; margin-bottom: 15px; }
        .date-info { font-size: 14px; }
        .date-info strong { margin-right: 10px; }
        .summary-table { width: 100%; margin: 30px 0; border-collapse: collapse; border: 1px solid ${borderColor}; }
        .summary-table thead { background: ${tableHeaderBg}; }
        .summary-table th { padding: 14px 16px; text-align: left; font-weight: 600; font-size: 13px; text-transform: uppercase; border: 1px solid ${borderColor}; }
        .summary-table tbody tr:nth-child(even) { background: #fafafa; }
        .summary-table td { padding: 12px 16px; border: 1px solid ${borderColor}; font-size: 14px; }
        .summary-table td.metric { font-weight: 600; }
        .summary-table td.value { font-weight: 700; font-size: 16px; }
        h2 { color: ${textColor}; font-size: 20px; font-weight: 700; margin: 35px 0 15px 0; padding-bottom: 10px; border-bottom: 2px solid ${borderColor}; }
        .detail-table { width: 100%; margin: 20px 0; border-collapse: collapse; border: 1px solid ${borderColor}; font-size: 13px; }
        .detail-table thead { background: ${tableHeaderBg}; }
        .detail-table th { padding: 12px 10px; text-align: left; font-weight: 600; font-size: 12px; text-transform: uppercase; border: 1px solid ${borderColor}; }
        .detail-table tbody tr:nth-child(even) { background: #fafafa; }
        .detail-table td { padding: 10px; border: 1px solid ${borderColor}; }
        .detail-table td strong { font-weight: 600; }
        .completion-rate { font-weight: 600; }
        .table-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        @media screen and (max-width: 768px) {
            body { padding: 8px; }
            .container { padding: 12px; }
            .header { margin: -12px -12px 16px -12px; padding: 16px 12px; }
            h1 { font-size: 20px; }
            h2 { font-size: 16px; margin: 20px 0 10px 0; }
            .summary-table th, .summary-table td { padding: 8px 10px; font-size: 12px; }
            .detail-table { min-width: 700px; }
        }
        @media print {
            @page { size: landscape; margin: 10mm; }
            body { padding: 0; }
            .container { border: none; padding: 10px; max-width: 100%; }
            .header { margin: -10px -10px 20px -10px; padding: 15px 10px; }
            .detail-table { font-size: 11px; }
            .detail-table th { font-size: 10px; padding: 6px 4px; }
            .detail-table td { padding: 6px 4px; }
        }
    </style>
</head>
<body>
    <div class='container'>
        <div class='header'>
            <h1>Training Summary Report</h1>
            <div class='date-info'>
                <strong>Period:</strong> ${formatDate(report.startDate)} to ${formatDate(report.endDate)}  |  
                <strong>Generated:</strong> ${formatDateTime(report.reportGeneratedDate)} UTC
            </div>
        </div>
        
        <h2>Summary</h2>
        <table class='summary-table'>
            <thead>
                <tr>
                    <th>Metric</th>
                    <th>Value</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td class='metric'>Total Assignments</td>
                    <td class='value'>${report.totalAssignments}</td>
                </tr>
                <tr>
                    <td class='metric'>Completed</td>
                    <td class='value'>${report.completedCount}</td>
                </tr>
                <tr>
                    <td class='metric'>Average Score</td>
                    <td class='value'>${report.averageScore.toFixed(1)}</td>
                </tr>
                <tr>
                    <td class='metric'>Passed</td>
                    <td class='value'>${report.passedCount}</td>
                </tr>
                <tr>
                    <td class='metric'>Failed</td>
                    <td class='value'>${report.failedCount}</td>
                </tr>
            </tbody>
        </table>

        ${moduleSummariesHtml}
        ${assignmentDetailsHtml}
    </div>
</body>
</html>`;
}

/**
 * Shared CSS styles used across all report HTML previews
 */
function getReportStyles(): string {
  const tableHeaderBg = '#f5f5f5';
  const textColor = '#000000';
  const borderColor = '#cccccc';

  return `
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; background: #ffffff; padding: 20px; color: ${textColor}; }
    .container { max-width: 1800px; margin: 0 auto; background: #ffffff; padding: 40px; border: 1px solid ${borderColor}; }
    .header { background: #ffffff; margin: -40px -40px 30px -40px; padding: 30px 40px; border-bottom: 2px solid ${borderColor}; color: ${textColor}; }
    h1 { font-size: 28px; font-weight: 700; margin-bottom: 15px; }
    .date-info { font-size: 14px; }
    .date-info strong { margin-right: 10px; }
    .summary-table { width: 100%; margin: 30px 0; border-collapse: collapse; border: 1px solid ${borderColor}; }
    .summary-table thead { background: ${tableHeaderBg}; }
    .summary-table th { padding: 14px 16px; text-align: left; font-weight: 600; font-size: 13px; text-transform: uppercase; border: 1px solid ${borderColor}; }
    .summary-table tbody tr:nth-child(even) { background: #fafafa; }
    .summary-table td { padding: 12px 16px; border: 1px solid ${borderColor}; font-size: 14px; }
    .summary-table td.metric { font-weight: 600; }
    .summary-table td.value { font-weight: 700; font-size: 16px; }
    h2 { color: ${textColor}; font-size: 20px; font-weight: 700; margin: 35px 0 15px 0; padding-bottom: 10px; border-bottom: 2px solid ${borderColor}; }
    .detail-table { width: 100%; margin: 20px 0; border-collapse: collapse; border: 1px solid ${borderColor}; font-size: 13px; }
    .detail-table thead { background: ${tableHeaderBg}; }
    .detail-table th { padding: 12px 10px; text-align: left; font-weight: 600; font-size: 12px; text-transform: uppercase; border: 1px solid ${borderColor}; }
    .detail-table tbody tr:nth-child(even) { background: #fafafa; }
    .detail-table td { padding: 10px; border: 1px solid ${borderColor}; }
    .detail-table td strong { font-weight: 600; }
    .completion-rate { font-weight: 600; }
  `;
}

/**
 * Wraps arbitrary report HTML body content in the shared styled document template.
 * Use this for audit/compliance reports to match the training report preview styling.
 *
 * @param title     Report title shown in the header (e.g. "Audit Summary Report")
 * @param periodInfo  Period line shown under the title (e.g. "2025-01-01 — 2025-12-31")
 * @param bodyHtml  Inner HTML for the report body (tables, headings, etc.) using summary-table / detail-table CSS classes
 */
export function wrapReportHtml(title: string, periodInfo: string, bodyHtml: string): string {
  return `<!DOCTYPE html>
<html lang='en'>
<head>
    <meta charset='utf-8'>
    <meta name='viewport' content='width=device-width, initial-scale=1.0'>
    <title>${title}</title>
    <style>${getReportStyles()}</style>
</head>
<body>
    <div class='container'>
        <div class='header'>
            <h1>${title}</h1>
            <div class='date-info'>
                <strong>Period:</strong> ${periodInfo}
            </div>
        </div>
        ${bodyHtml}
    </div>
</body>
</html>`;
}

/**
 * Generates HTML preview for Training Completion Report
 */
export function generateCompletionHtmlPreview(report: TrainingCompletionReportDto): string {
  // Plain styling without colors
  const headerBg = '#ffffff';
  const tableHeaderBg = '#f5f5f5';
  const textColor = '#000000';
  const borderColor = '#cccccc';

  return `
<!DOCTYPE html>
<html lang='en'>
<head>
    <meta charset='utf-8'>
    <meta name='viewport' content='width=device-width, initial-scale=1.0'>
    <title>Training Completion Report</title>
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; background: #ffffff; padding: 20px; color: ${textColor}; }
        .container { max-width: 1800px; margin: 0 auto; background: #ffffff; padding: 40px; border: 1px solid ${borderColor}; }
        .header { background: ${headerBg}; margin: -40px -40px 30px -40px; padding: 30px 40px; border-bottom: 2px solid ${borderColor}; color: ${textColor}; }
        h1 { font-size: 28px; font-weight: 700; margin-bottom: 15px; }
        .date-info { font-size: 14px; }
        .date-info strong { margin-right: 10px; }
        .summary-table { width: 100%; margin: 30px 0; border-collapse: collapse; border: 1px solid ${borderColor}; }
        .summary-table thead { background: ${tableHeaderBg}; }
        .summary-table th { padding: 14px 16px; text-align: left; font-weight: 600; font-size: 13px; text-transform: uppercase; border: 1px solid ${borderColor}; }
        .summary-table tbody tr:nth-child(even) { background: #fafafa; }
        .summary-table td { padding: 12px 16px; border: 1px solid ${borderColor}; font-size: 14px; }
        .summary-table td.metric { font-weight: 600; }
        .summary-table td.value { font-weight: 700; font-size: 16px; }
        h2 { color: ${textColor}; font-size: 20px; font-weight: 700; margin: 35px 0 15px 0; padding-bottom: 10px; border-bottom: 2px solid ${borderColor}; }
        .detail-table { width: 100%; margin: 20px 0; border-collapse: collapse; border: 1px solid ${borderColor}; font-size: 11px; table-layout: fixed; }
        .detail-table thead { background: ${tableHeaderBg}; }
        .detail-table th { padding: 8px 6px; text-align: left; font-weight: 600; font-size: 10px; text-transform: uppercase; border: 1px solid ${borderColor}; white-space: nowrap; }
        .detail-table tbody tr:nth-child(even) { background: #fafafa; }
        .detail-table td { padding: 6px; border: 1px solid ${borderColor}; overflow: hidden; text-overflow: ellipsis; }
        .detail-table td strong { font-weight: 600; }
        .detail-table td.nowrap { white-space: nowrap; }
        .detail-table td.text-col { white-space: normal; word-break: break-word; }
        .detail-table th.center, .detail-table td.center { text-align: center; }
        .table-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        @media screen and (max-width: 768px) {
            body { padding: 8px; }
            .container { padding: 12px; }
            .header { margin: -12px -12px 16px -12px; padding: 16px 12px; }
            h1 { font-size: 20px; }
            h2 { font-size: 16px; margin: 20px 0 10px 0; }
            .summary-table th, .summary-table td { padding: 8px 10px; font-size: 12px; }
            .detail-table { min-width: 800px; }
        }
        @media print {
            @page { size: landscape; margin: 10mm; }
            body { padding: 0; }
            .container { border: none; padding: 10px; max-width: 100%; }
            .header { margin: -10px -10px 20px -10px; padding: 15px 10px; }
            .detail-table { font-size: 9px; }
            .detail-table th { font-size: 8px; padding: 4px 3px; }
            .detail-table td { padding: 4px 3px; }
        }
    </style>
</head>
<body>
    <div class='container'>
        <div class='header'>
            <h1>Training Completion Report</h1>
            <div class='date-info'>
                <strong>Period:</strong> ${formatDate(report.startDate)} to ${formatDate(report.endDate)}  |  
                <strong>Generated:</strong> ${formatDateTime(report.reportGeneratedDate)} UTC
            </div>
        </div>
        
        <h2>Summary</h2>
         <div class='table-scroll'>
        <table class='summary-table'>
            <thead>
                <tr>
                    <th>Metric</th>
                    <th>Value</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td class='metric'>Total Completions</td>
                    <td class='value'>${report.totalCompletions}</td>
                </tr>
                <tr>
                    <td class='metric'>Average Score</td>
                    <td class='value'>${report.averageScore.toFixed(1)}</td>
                </tr>
                
            </tbody>
        </table>
        </div>

        <h2>User Completions</h2>
        <div class='table-scroll'>
        <table class='detail-table'>
            <colgroup>
            <col style='width: 11%'>
            <col style='width: 9%'>
            <col style='width: 10%'>
                <col style='width: 7%'>
                <col style='width: 7%'>
                <col style='width: 11%'>
                <col style='width: 8%'>
                <col style='width: 5%'>
                <col style='width: 6%'>
                <col style='width: 6%'>
            </colgroup>
            <thead>
                <tr>
                <th>Module</th>
                <th>Category</th>
                    <th>User</th>
                    <th>Assigned</th>
                    <th>Due</th>
                    <th>Completed</th>
                    <th>Score</th>
                    <th>Status</th>
                    <th class='center'>Attempts</th>
                    <th class='center'>Certificate</th>
                </tr>
            </thead>
            <tbody>
                ${report.userCompletions.map(c => `
                <tr>
                <td class='text-col'><strong>${escapeHtml(c.moduleName)}</strong></td>
                <td class='text-col'>${escapeHtml(c.categoryName)}</td>
                    <td class='text-col'>${escapeHtml(c.userName)}</td>
                    <td class='nowrap'>${formatDate(c.assignedDate)}</td>
                    <td class='nowrap'>${c.dueDate ? formatDate(c.dueDate) : 'N/A'}</td>
                    <td class='nowrap'>${formatDateTime(c.completedDate)}</td>
                    <td class='nowrap'><strong>${c.score.toFixed(1)}</strong></td>
                    <td class='nowrap'>
                        <span style='font-weight: 600;'>
                            ${c.passed ? 'PASSED' : 'FAILED'}
                        </span>
                    </td>
                    <td class='center'>${c.attemptsCount}</td>
                    <td class='center'>${c.hasCertificate ? '✓' : '—'}</td>
                </tr>`).join('')}
            </tbody>
        </table>
        </div>
    </div>
</body>
</html>`;
}
